import React, { useState } from 'react';
import { Facebook, Instagram, Mail, Phone, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import { contactEmails, iceLogo } from '../mock';

export default function Footer() {
  const [email, setEmail] = useState('');
  const { toast } = useToast();

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    toast({ title: 'Subscribed!', description: `Welcome to the Patriots family, ${email}` });
    setEmail('');
  };

  return (
    <footer className="bg-[#060f2a] text-white pt-16 relative overflow-hidden">
      <div className="absolute inset-0 stars-bg opacity-50" />
      <div className="stripe-sep absolute top-0 left-0 right-0" />

      <div className="relative max-w-7xl mx-auto px-6 grid md:grid-cols-4 gap-10 pb-12">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img src={iceLogo} alt="ICE logo" className="h-14 w-auto object-contain" />
            <div>
              <div className="font-display text-xl tracking-wider">BROOKLINE JP</div>
              <div className="font-heading text-[#c8102e] text-xs tracking-[0.3em]">PATRIOTS</div>
            </div>
          </div>
          <p className="text-white/70 text-sm leading-relaxed">
            20 years of Pop-Warner Football & Cheerleading in the Brookline / Jamaica Plain community. Building young men and women through teamwork, discipline & heart.
          </p>
        </div>

        <div>
          <h4 className="font-heading uppercase tracking-widest text-[#c8102e] mb-4">Quick Links</h4>
          <ul className="space-y-2 text-sm text-white/70">
            <li><Link to="/about" data-testid="footer-about-link" className="inline-flex items-center hover:text-white transition-colors">About Us</Link></li>
            <li><Link to="/team" data-testid="footer-team-link" className="inline-flex items-center hover:text-white transition-colors">Team & Coaches</Link></li>
            <li><Link to="/alumni" data-testid="footer-alumni-link" className="inline-flex items-center hover:text-white transition-colors">Alumni</Link></li>
            <li><Link to="/events" data-testid="footer-events-link" className="inline-flex items-center hover:text-white transition-colors">Events</Link></li>
            <li><Link to="/announcements" data-testid="footer-announcements-link" className="hover:text-white transition-colors">Announcements &amp; Weather</Link></li>
            <li><Link to="/football-practice-times" data-testid="footer-practice-times-link" className="hover:text-white transition-colors">Football Practice Time</Link></li>
            <li><Link to="/contact" data-testid="footer-contact-link" className="inline-flex items-center hover:text-white transition-colors">Contact</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-heading uppercase tracking-widest text-[#c8102e] mb-4">Contact</h4>
          <ul className="space-y-3 text-sm text-white/70">
            <li className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5 text-[#c8102e]" /> 617-818-8296</li>
            <li className="flex items-start gap-2" data-testid="footer-contact-emails">
              <Mail className="w-4 h-4 mt-0.5 text-[#c8102e] shrink-0" />
              <span className="min-w-0">
                {contactEmails.map((email, index) => (
                  <a key={email} href={`mailto:${email}`} data-testid={`footer-contact-email-${index + 1}`} className="block break-all hover:text-white transition-colors">{email}</a>
                ))}
              </span>
            </li>
            <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[#c8102e]" /> Brookline / Jamaica Plain, MA</li>
          </ul>
          <div className="flex gap-3 mt-5">
            <a href="https://www.facebook.com/627656147646539" target="_blank" rel="noreferrer" data-testid="footer-facebook-link" aria-label="Brookline JP Patriots on Facebook" className="w-11 h-11 flex items-center justify-center bg-white/5 hover:bg-[#c8102e] transition-colors">
              <Facebook className="w-4 h-4" />
            </a>
            <a href="https://www.instagram.com/brooklinejppats" target="_blank" rel="noreferrer" data-testid="footer-instagram-link" aria-label="Brookline JP Patriots on Instagram" className="w-11 h-11 flex items-center justify-center bg-white/5 hover:bg-[#c8102e] transition-colors">
              <Instagram className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div>
          <h4 className="font-heading uppercase tracking-widest text-[#c8102e] mb-4">Newsletter</h4>
          <p className="text-sm text-white/70 mb-3">Stay updated on games, events & registration.</p>
          <form onSubmit={handleSubscribe} className="flex flex-col gap-2">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              autoComplete="email"
              inputMode="email"
              data-testid="footer-newsletter-email"
              className="bg-white/5 border border-white/15 px-3 py-2.5 text-sm placeholder-white/40 focus:outline-none focus:border-[#c8102e]"
            />
            <button type="submit" data-testid="footer-newsletter-submit" className="bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest text-sm py-2.5 transition-colors">
              Sign Up
            </button>
          </form>
        </div>
      </div>

      <div className="stripe-sep" />
      <div className="py-5 text-center text-xs text-white/50 font-heading tracking-wider">
        © 2024 &quot;ICE&quot; BROOKLINE JP PATRIOTS — ALL RIGHTS RESERVED
      </div>
    </footer>
  );
}
